import joinURL from '../common/joinURL.js'

export default function resourcePrefetch({document, XMLHttpRequest, Promise, santaBase},
  {siteModel, publicModel, pageInfo, shouldTimeout, wixBiSession, isServerSide, mobileView, experiment, requirejs}) {
  if (!publicModel) {
    return {
      contentCache() {},
      setContext() {}
    }
  }

  const cache = {}
  const pending = {}
  const context = {
    shouldDelay() {}
  }

  function _prefetch(url, options, pageId) {
    const r = new XMLHttpRequest()

    r.onload = function () {
      if (this.status >= 200 && this.status < 300) {
        const cached = options.parse ? options.parse(r.response) : r.response
        cache[url] = cached
        pending[url].filter(op =>
          op.success
        ).forEach(op => {
          op.success(cached)
        })
      } else {
        this.onerror()
      }
    }
    r.onerror = function () {
      cache[url] = r
      pending[url].filter(op =>
        op.error
      ).forEach(op => {
        op.error(r)
      })
    }
    r.onloadend = function () {
      delete pending[url]
    }

    r.open('GET', url, true)

    //Property timeout MUST be after .open() and before .send()
    //This is related to an issue with IE - https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest/timeout
    if (shouldTimeout) {
      r.timeout = 2000
      r.ontimeout = function () {
        if (pageId) {
          //beat 2 = 'reset' event
          wixBiSession.beat(2, pageId)
        }
      }
    }
    //Property timeout MUST be after .open() and before .send()

    if (options.accept) {
      r.setRequestHeader('Accept', options.accept)
    }

    r.send()
  }

  function prefetch(url, options, pageId) {
    if (url) {
      options = options || {}
      const cached = cache[url]
      if (cached) {
        if (options.success) {
          options.success(cached)
        }
      } else {
        let p = pending[url]
        if (!p) {
          pending[url] = p = []
          _prefetch(url, options, pageId)
        }
        p.push(options)
      }
    }
  }

  function prefetchJSON(url, pageId) {
    prefetch(url, {
      accept: 'application/json',
      parse(response) {
        let json = null
        try {
          json = JSON.parse(response)
          if (wixBiSession.et < 6) {
            prefetchComps(json)
            prefetchSvg(json.structure, response)
            earlyBackground(json, pageId)
          }
        } catch (e) {
          // empty
        }
        return json
      }
    }, pageId)
  }

  let pc = null
  function prefetchComps(json) {
    if (Promise && json.structure && experiment.isOpen('prefetchComps')) {
      if (pc === null) {
        pc = new Promise(resolve => {
          requirejs(['coreUtils'], ({prefetchComponents}) => {
            resolve(prefetchComponents)
          })
        })
      }
      pc.then(prefetchComponents => {
        const hasBlog = prefetchComponents(json, siteModel, mobileView)
        if (hasBlog) {
          prefetchJSON(joinURL(santaBase, 'static/wixapps/apps/blog/descriptor.json'))
        }
      })
    }
  }

  function toGrayscale(color) {
    const match = color.match(/#(.{2})(.{2})(.{2})/)
    if (match) {
      const c = Math.floor(match.slice(1).map(h => parseInt(h, 16)).reduce((s, v) => s + v, 0) / 3).toString(16)
      return `#${c}${c}${c}`
    }
  }

  function headerColor(json) {
    if (experiment.isOpen('earlyBackgroundUseHeader')) {
      const {structure: {children = []} = {}} = json
      const header = children.find(({id}) => id === 'SITE_HEADER')
      if (header) {
        const {styleId} = header
        const {data: {theme_data: {[styleId]: {style: {properties: {bg} = {}} = {}} = {}} = {}} = {}} = json
        return bg
      }
    }
  }
  function pageColor(document_data, pageId) {
    if (document_data) {
      const {[pageId]: {pageBackgrounds: {[mobileView ? 'mobile' : 'desktop']: {ref} = {}} = {}} = {}} = document_data
      if (ref) {
        const {[ref[0] === '#' ? ref.substring(1) : ref]: {color} = {}} = document_data
        return color
      }
    }
  }

  let savedColor
  let savedMap
  function earlyBackground(json, pageId) {
    if (document && experiment.isOpen('earlyBackground')) {
      const {data: {document_data, theme_data} = {}} = json
      if (theme_data && !savedMap) {
        ({THEME_DATA: {color: savedMap} = {}} = theme_data)
      }
      savedColor = headerColor(json) || savedColor || pageColor(document_data, pageId)
      if (savedColor) {
        const match = savedColor.match(/color_(\d+)/)
        if (match) {
          savedColor = savedMap[match[1]]
        }
        if (savedColor && savedColor[0] === '#') {
          document.body.style.backgroundColor = experiment.isOpen('earlyBackgroundGrayscale') ? toGrayscale(savedColor) : savedColor
        }
      }
    }
  }

  function shapeSkinNameToUrl(shapeSkinName) {
    function getSvgUri(version, svgHash, shapeName) {
      return svgHash + (version === 1 ? '_svgshape.v1.' + shapeName : '') + '.svg' //eslint-disable-line prefer-template
    }

    const mediaRootUrl = siteModel.serviceTopology.mediaRootUrl
    const partsArr = shapeSkinName.replace(/^.*\//, '').split('.')
    const version = partsArr[1] === 'v1' ? 1 : 2
    const svgHash = partsArr[2].replace(/svg_/i, '')
    const svgName = partsArr[3]
    const svgUri = getSvgUri(version, svgHash, svgName)
    return joinURL(mediaRootUrl, 'shapes', svgUri)
  }

  const svgMap = {
    'skins.viewer.svgshape.SvgShapeDefaultSki': true
  }

  function prefetchSvg(structure, response) {
    if (structure && response.indexOf('"svg') !== -1) {
      const svgs = JSON.stringify(structure).match(/"skin"\s*:\s*"svg[^"]+"/g)
      if (svgs) {
        svgs
          .map(m => {
            m = m.match(/svg[^"]+/)
            return m && m[0]
          })
          .filter(svg => {
            if (!svg || svgMap[svg]) {
              return false
            }
            svgMap[svg] = true
            return true
          })
          .map(shapeSkinNameToUrl)
          .forEach(url => {
            prefetch(url)
          })
      }
    }
  }

  function getFirstUrl(pageJsonFileName) {
    if (pageJsonFileName) {
      const topology = publicModel.pageList.topology[0]
      return topology.baseUrl + topology.parts.replace('{filename}', pageJsonFileName)
    }
  }

  function getMasterPageFirstURL() {
    const {pageList: {masterPageJsonFileName, masterPage}} = publicModel
    return getFirstUrl(masterPageJsonFileName) || masterPage && masterPage[0]
  }

  function getPageFirstURL() {
    return getFirstUrl(pageInfo.pageJsonFileName) || pageInfo.urls[0]
  }

  function shouldPrefetchPagesJsons() {
    return !isServerSide
  }

  try {
    if (shouldPrefetchPagesJsons()) {
      prefetchJSON(getMasterPageFirstURL(), 'masterPage')
      if (pageInfo) {
        prefetchJSON(getPageFirstURL(), pageInfo.pageId)
      }
    }
  } catch (e) {
    // empty
  }

  let cbs = []
  function async(cb) {
    function delay() {
      const top = cbs.shift()
      if (top) {
        top()
        if (cbs.length > 0) {
          setTimeout(delay, 10)
        }
      }
    }

    if (cbs.push(cb) === 1) {
      if (context.shouldDelay()) {
        setTimeout(delay, 10)
      } else {
        setTimeout(() => {
          const fs = cbs
          cbs = []
          fs.forEach(f => {
            f()
          })
        }, 0)
      }
    }
  }

  return {
    contentCache(options) {
      const {url} = options
      const cached = cache[url]
      if (cached) {
        async(() => {
          if (cached instanceof XMLHttpRequest) {
            options.error(cached)
          } else {
            options.success(cached)
          }
        })
      } else if (pending[url]) {
        pending[url].push(options)
      } else {
        return false
      }
      return true
    },

    setContext(update) {
      Object.keys(update).forEach(key => {
        context[key] = update[key]
      })
      // Object.assign(context, update)
    }
  }
}



// WEBPACK FOOTER //
// ./node_modules/santa-main-r/src/lib/viewer/resourcePrefetch.js